import React, {Component} from 'react';
import ListDesignerHoc from "../../../../../HocHelpers/ListDesignerHoc";
import {ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend} from "recharts";
import {chartColors} from "../../../../../Utils/ChartUtils/chartColors";
import _ from "lodash";
import {ChartTitle} from "../../../../../Utils/ChartUtils/ChartTitle";
import {
    findColor, getColumnById, getColumnTypeById, humanizeDuration, renderCustomizedLabel,
    transformData
} from "../../../../../Utils/ChartUtils/chartUtils";
import IconButton from "@material-ui/core/IconButton";
import FilterListIcon from '@material-ui/icons/FilterList';
import Icon from "@material-ui/core/Icon";
import formatValue from "../../../../../Utils/ListUtils/FormatValue";

const RADIAN = Math.PI / 180;
const cx = 150;
const cy = 200;
const iR = 50;
const oR = 100;
const NEEDLE_COLOR = "#000"

const needle = (value, data, cx, cy, iR, oR, color) => {
    let total = 0;
    data.forEach((v) => {
        total += v.value;
    });
    const ang = 180.0 * (1 - value / total);
    const length = (iR + 2 * oR) / 3;
    const sin = Math.sin(-RADIAN * ang);
    const cos = Math.cos(-RADIAN * ang);
    const r = 5;
    const x0 = cx + 5;
    const y0 = cy + 5;
    const xba = x0 + r * sin;
    const yba = y0 - r * cos;
    const xbb = x0 - r * sin;
    const ybb = y0 + r * cos;
    const xp = x0 + length * cos;
    const yp = y0 + length * sin;

    return [
        <circle cx={x0} cy={y0} r={r} fill={color} stroke="none"/>,
        <path d={`M${xba} ${yba}L${xbb} ${ybb} L${xp} ${yp} L${xba} ${yba}`} stroke="#none" fill={color}/>,
    ];
};
const Needle = ({ cx, cy, midAngle }) => {
    const NEEDLE_BASE_RADIUS_PX = 5;
    const NEEDLE_LENGTH_PX = (iR + 2 * oR) / 3;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const xp = cx + NEEDLE_LENGTH_PX * cos;
    const yp = cy + NEEDLE_LENGTH_PX * sin;
    return (
        <g>
            <circle
                cx={cx}
                cy={cy}
                r={NEEDLE_BASE_RADIUS_PX}
                fill={NEEDLE_COLOR}
                stroke="none"
            />
            <path
                d={`M${cx} ${cy}L${cx} ${cy} L${xp} ${yp}`}
                strokeWidth={2}
                stroke={NEEDLE_COLOR}
                fill={NEEDLE_COLOR}
            />
        </g>
    );
};
class PieNarrowRechart extends Component {
    constructor(props) {
        super(props);
        this.state = {
            listData: [],
            props: {
                legend: {}
            }
        }
    }

    componentDidMount() {
        const {
            data: {columns, chartProps},
            listData: {content = []} = {},
            pivotData = [], pivotColumns = []
        } = this.props;
        let currentData = pivotData.length > 0 ? pivotData : content;
        let currentColumns = pivotColumns.length > 0 ? pivotColumns : columns;
        let newContent = transformData(currentData, currentColumns);
        this.setState({listData: newContent, props: {...chartProps}, columns: currentColumns})
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        const {
            data: {columns, chartProps},
            listData: {content} = {},
            pivotData = [], pivotColumns = []
        } = this.props;
        const {
            data: {chartProps: prevChartProps, columns: prevColumns} = {},
            listData: {content: prevContent},
            pivotData: prevPivotData = [], pivotColumns: prevPivotColumns = []
        } = prevProps;
        let currentData = pivotData.length > 0 ? pivotData : content;
        let currentColumns = pivotColumns.length > 0 ? pivotColumns : columns;
        let prevCurrentData = prevPivotData.length > 0 ? prevPivotData : prevContent;
        let prevCurrentColumns = prevPivotColumns.length > 0 ? prevPivotColumns : prevColumns;

        if (currentData !== prevCurrentData) {
            let newContent = transformData(currentData, currentColumns);
            this.setState({
                listData: newContent,
                columns: currentColumns
            })
        }

        if (currentColumns !== prevCurrentColumns) {
            let newContent = transformData(currentData, currentColumns);
            this.setState({props: chartProps, listData: newContent})
        }

        //when chart props was changed
        if (!_.isEmpty(chartProps) && chartProps !== prevChartProps) {
            let newContent = transformData(currentData, currentColumns);
            this.setState({props: chartProps, listData: newContent})
        }
    }

    render() {
        const {
            listData,
            columns,
            props: {
                series = [],
                valueColumn = '',
                labelColumn = '',
                enableRadialLabels = true,
                showNameInLabel,
                paddingAngle,
                innerRadius,
                legend: {
                    enable: legendEnable,
                    position: legendPosition,
                    verticalAlign,
                    direction,
                    itemHeight,
                    itemWidth,
                    translateY,
                    translateX
                }
            }
        } = this.state;
        const {
            data: {
                name,
                chartProps: {
                    showAdvancedFilter,
                    hideTitle,
                    title: {
                        styles: {
                            fontSize: {size, type} = {},
                            background: titleBackground,
                            color,
                            fontStyle, fontWeight, textDecoration, textAlign
                        } = {}
                    }
                }
            }
        } = this.props;
        const args = {
            cx: `calc(100%-${cx}px)`,
            cy,
            data: listData.filter(v => {
                return v[valueColumn] !== undefined
            }),
            dataKey: valueColumn,
            endAngle: 0,
            fill: "#8884d8",
            innerRadius: innerRadius || iR,
            outerRadius: oR,
            startAngle: 180,
            stroke: "none",
        };
        return (
            <div style={{height: "inherit"}}>
                <div style={{display: 'flex'}}>
                    <div style={{flexGrow: 1}}>
                        <ChartTitle
                            hideLabel={hideTitle}
                            size={size}
                            type={type}
                            fontWeight={fontWeight}
                            fontStyle={fontStyle}
                            textDecoration={textDecoration}
                            textAlign={textAlign}
                            title={name}
                            color={color}
                            background={titleBackground}
                        />
                    </div>
                    {showAdvancedFilter && (
                        <IconButton style={{padding: 2}} disabled>
                            <Icon style={{margin: 5}}>
                                <FilterListIcon/>
                            </Icon>
                        </IconButton>
                    )}
                </div>
                <ResponsiveContainer>
                    <PieChart
                        margin={{
                            top: 150, right: 30, left: 30, bottom: 87,
                        }}
                    >
                        <Tooltip
                            labelFormatter={(value) => {
                                const columnType = getColumnTypeById(columns, valueColumn);
                                const columnFormat = getColumnById(columns, valueColumn).format;
                                return columnType === 'duration' ?
                                    humanizeDuration(value, columnFormat) :
                                    formatValue(value, columnType, columnFormat)
                            }}
                            formatter={(value) => {
                                const columnType = getColumnTypeById(columns, valueColumn);
                                const columnFormat = getColumnById(columns, valueColumn).format;
                                return columnType === 'duration' ?
                                    humanizeDuration(value, columnFormat) :
                                    formatValue(value, columnType, columnFormat)
                            }}
                        />
                        <Pie
                            data={listData.filter(v => {
                                return v[valueColumn] !== undefined
                            })}
                            paddingAngle={paddingAngle}
                            innerRadius={innerRadius || iR}
                            outerRadius={oR}
                            nameKey={labelColumn}
                            fill="#8884d8"
                            label={enableRadialLabels ? (v) => renderCustomizedLabel(v, columns, valueColumn, showNameInLabel) : false}
                            animationDuration={300}
                            {...args}
                        >
                            {
                                listData.map((entry, index) => {
                                    const customColor = findColor(series, entry[labelColumn]);
                                    return (
                                        <Cell key={`cell-${index}`}
                                              fill={customColor ? customColor : chartColors[index % chartColors.length]}/>
                                    )
                                })
                            }
                        </Pie>
                        <Pie
                            {...args}
                            dataKey={valueColumn}
                            activeIndex={0}
                            activeShape={Needle}
                            fill="none"
                        />
                        {/*{needle(0, listData.map(v => {*/}
                        {/*    return {value: v[valueColumn]}*/}
                        {/*}), cx, cy, iR, oR, '#000')}*/}
                        {legendEnable && (
                            <Legend
                                align={legendPosition}
                                verticalAlign={verticalAlign}
                                layout={direction}
                                height={parseInt(itemHeight)}
                                width={parseInt(itemWidth)}
                                wrapperStyle={{
                                    paddingLeft: translateX ? `${translateX}px` : 0,
                                    paddingTop: translateY ? `${translateY}px` : 0
                                }}
                            />
                        )}
                    </PieChart>
                </ResponsiveContainer>
            </div>
        );
    }
}

export default ListDesignerHoc(PieNarrowRechart);